'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import "./REDBreadcrumb.css";

export type BreadCrumbItem = {
  label: string;
  href?: string; // last item often has no href
};

type Props = {
  breadCrumbData: BreadCrumbItem[];
  home?: { label?: string; href?: string };
  ariaLabel?: string;
};

export default function REDBreadcrumb({
  breadCrumbData,
  home,
  ariaLabel,
}: Props) {
  const { t } = useTranslation('navigation');

  // Use translation with fallback to provided values
  const homeConfig = {
    label: home?.label || t('home'),
    href: home?.href || '/'
  };
  const breadcrumbAriaLabel = ariaLabel || t('breadcrumbAriaLabel');
  const items = React.useMemo(() => {
    // normalize: ensure last item has no href (current page)
    if (!breadCrumbData?.length) return [];
    const cloned = breadCrumbData.map((x) => ({ ...x }));
    cloned[cloned.length - 1].href = undefined;
    return cloned;
  }, [breadCrumbData]);

  return (
    // EXACT markup & classes you provided; Tailwind utility classes are ADDED
    <nav aria-label={breadcrumbAriaLabel} className="breadcrumb-overview">
      <ol className="breadcrumb__list hidden md:flex">
        {/* Home */}
        <li className="breadcrumb__item">
          <a className="breadcrumb__home" href={homeConfig.href}>
            {homeConfig.label}
            <span className="sr-only">{homeConfig.label}</span>
          </a>
        </li>

        {/* Dynamic items */}
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <li key={`${item.label}-${idx}`} className="breadcrumb__item">
              {isLast || !item.href ? (
                <span aria-current="page">{item.label}</span>
              ) : (
                <a href={item.href}>{item.label}</a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
